import { useMutation as useApolloMutation } from '@apollo/client';
import {
  useMutation as useTanstackMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useContext } from 'react';
import type { ChangeRequestConfirmContextValue } from 'src/providers/change-request-confirm-context';
import { ChangeRequestConfirmContext } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '@/hooks/mutations/_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '@/hooks/mutations/invalidateChangeRequestFeeds';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type {
  MutationHookConfig,
  MutationHookFactoryResult,
  MutationHookResult,
} from './types';

/**
 * Factory that creates a GraphQL-to-tRPC mutation hook — the mutation
 * counterpart of `createQueryHook`. It collapses the previous two-file pattern
 * (`useXxx.tsx` wrapper + `useXxxTRPC.tsx`) into a single declarative config.
 *
 * The returned hook:
 * - switches between the Apollo mutation and the tRPC mutation on the `trpc` flag,
 * - preserves the per-entity method name (so consumers are untouched),
 * - keeps cache-invalidation parity (Apollo cache eviction ↔ tanstack `invalidateQueries`).
 *
 * Change-request behaviour is opt-in (`isChangeRequestGated` /
 * `refreshChangeRequestFeedsWhen`). The factory picks a **governed** or a
 * **plain** hook body at creation time, so a plain mutation never reads the
 * change-request confirm context at render — only governed hooks depend on it.
 *
 * It deliberately does NOT add UI side-effects (toasts) or pass
 * `throwOnError` — `FormContext` owns mutation UX (see packages/web/CLAUDE.md).
 */
export const createMutationHook = <
  TInput,
  TTrpcInput,
  TTrpcResult,
  TGraphQLOutput,
  TName extends string,
  TOptions = void,
>(
  config: MutationHookConfig<
    TInput,
    TTrpcInput,
    TTrpcResult,
    TGraphQLOutput,
    TName,
    TOptions
  >
): MutationHookFactoryResult<TName, TInput, TGraphQLOutput, TOptions> => {
  const {
    name,
    mutationOptions,
    mapInput,
    mapResponse,
    invalidate,
    isChangeRequestGated,
    refreshChangeRequestFeedsWhen,
    graphqlDocument,
    graphqlVariables,
    evictFields,
    refetchQueries,
    apolloContext,
  } = config;

  // Per-render setup shared by the governed and plain hook bodies.
  const useMutationCore = () => {
    const trpcEnabled = useIsFeatureFlagEnabled('trpc');
    const trpc = useTRPC();
    const queryClient = useQueryClient();

    // GraphQL mutation (flag off)
    const [graphqlMutation, graphqlState] = useApolloMutation(graphqlDocument, {
      update: evictFields
        ? (cache) => {
            // Evict every field, then a single gc() pass — cheaper than the
            // per-field gc() the shared evictField helper does (RSP-6326 review).
            evictFields.forEach((fieldName) =>
              cache.evict({ id: 'ROOT_QUERY', fieldName })
            );
            cache.gc();
          }
        : undefined,
      refetchQueries,
    });

    // tRPC mutation (flag on)
    const onSuccess = async (result: TTrpcResult): Promise<void> => {
      if (refreshChangeRequestFeedsWhen?.(result)) {
        await invalidateChangeRequestFeeds(queryClient, trpc);
      }
      for (const queryKey of invalidate?.(trpc) ?? []) {
        await queryClient.invalidateQueries({ queryKey });
      }
    };
    const trpcMutation = useTanstackMutation(mutationOptions(trpc, onSuccess));

    return { trpcEnabled, graphqlMutation, graphqlState, trpcMutation };
  };

  // Assembles the public `{ [name], loading, error }` from the per-render core
  // plus the resolved change-request confirm context (null for plain hooks).
  // Not a hook — pure assembly — so both variants share the run() logic.
  const buildResult = (
    core: ReturnType<typeof useMutationCore>,
    changeRequestConfirm: ChangeRequestConfirmContextValue | null
  ): MutationHookResult<TName, TInput, TGraphQLOutput, TOptions> => {
    const { trpcEnabled, graphqlMutation, graphqlState, trpcMutation } = core;

    const run = async (
      input: TInput,
      options?: TOptions
    ): Promise<TGraphQLOutput> => {
      if (trpcEnabled) {
        const trpcInput = mapInput(input);

        let result: TTrpcResult;
        if (isChangeRequestGated) {
          if (!changeRequestConfirm) {
            throw new Error(
              `${name}: change-request-gated mutation requires ChangeRequestConfirmProvider`
            );
          }
          result = await withChangeRequestConfirm({
            input: trpcInput,
            run: (variables) => trpcMutation.mutateAsync(variables),
            confirm: changeRequestConfirm,
          });
        } else {
          result = await trpcMutation.mutateAsync(trpcInput);
        }

        return mapResponse(result, input);
      }

      const graphqlResult = await graphqlMutation({
        variables: graphqlVariables(input, options),
        context: apolloContext?.(options),
      });
      if (!graphqlResult.data) {
        throw new Error(`Failed to run ${name}`);
      }

      return graphqlResult.data;
    };

    // The mapped return type names the mutate fn per-entity (e.g. `updateAction`);
    // a computed-key object literal can't be inferred as that mapped type.
    return {
      [name]: run,
      loading: trpcEnabled ? trpcMutation.isPending : graphqlState.loading,
      error: trpcEnabled ? trpcMutation.error : (graphqlState.error ?? null),
    } as MutationHookResult<TName, TInput, TGraphQLOutput, TOptions>;
  };

  // Governed hooks read the change-request confirm context; plain hooks never
  // touch it. The choice is fixed at factory-creation time, so each returned
  // hook has a stable hook-call order.
  const useGovernedMutationHook = () =>
    buildResult(useMutationCore(), useContext(ChangeRequestConfirmContext));
  const usePlainMutationHook = () => buildResult(useMutationCore(), null);

  return isChangeRequestGated ? useGovernedMutationHook : usePlainMutationHook;
};
